// frontend/src/components/public-dosing/ContainerCard.tsx
import {
  Paper,
  TextInput,
  NumberInput,
  Table,
  ActionIcon,
  Group,
  Text,
  Badge,
} from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { ElementSelector } from './ElementSelector';
import type { PublicDosingContainer, PublicDosingElement } from '@/types/publicDosing';

const BADGE_COLORS = ['blue', 'grape', 'green', 'orange', 'cyan', 'teal'];

interface ContainerCardProps {
  container: PublicDosingContainer;
  index: number;
  days: number;
  onUpdate: (index: number, updates: Partial<PublicDosingContainer>) => void;
  onRemove: (index: number) => void;
  onAddElement: (containerIndex: number, elementName: string) => void;
  onUpdateElement: (
    containerIndex: number,
    elementIndex: number,
    updates: Partial<PublicDosingElement>
  ) => void;
  onRemoveElement: (containerIndex: number, elementIndex: number) => void;
}

export function ContainerCard({
  container,
  index,
  days,
  onUpdate,
  onRemove,
  onAddElement,
  onUpdateElement,
  onRemoveElement,
}: ContainerCardProps) {
  const containerTotal = container.ml_per_day * days;
  const elementsTotal = container.elements.reduce(
    (sum, el) => sum + el.ml_per_day * days,
    0
  );
  const rodiWater = Math.max(0, containerTotal - elementsTotal);
  const overAllocated = elementsTotal > containerTotal;

  const existingElements = container.elements.map((el) => el.element_name);

  return (
    <Paper withBorder>
      {/* Header */}
      <Group
        gap="md"
        wrap="wrap"
        p="md"
        style={{ backgroundColor: 'var(--mantine-color-gray-0)', borderBottom: '1px solid var(--mantine-color-gray-3)' }}
      >
        <TextInput
          value={container.name}
          onChange={(e) => onUpdate(index, { name: e.target.value })}
          variant="filled"
          fw={600}
          size="md"
          style={{ width: 200 }}
          placeholder="Container name"
          aria-label="Container name"
        />
        <Group gap="xs">
          <Text size="sm" c="dimmed">ml/day:</Text>
          <NumberInput
            value={container.ml_per_day}
            onChange={(val) => onUpdate(index, { ml_per_day: Number(val) || 0 })}
            min={0.1}
            step={0.1}
            decimalScale={1}
            size="xs"
            style={{ width: 70 }}
            aria-label="Milliliters per day"
          />
        </Group>
        <ActionIcon variant="subtle" color="gray" onClick={() => onRemove(index)} aria-label="Remove container">
          <IconX size={16} />
        </ActionIcon>
      </Group>

      {/* Elements Table */}
      <div style={{ padding: 16 }}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: '40%' }}>Element</Table.Th>
              <Table.Th style={{ width: '25%' }}>ml/day</Table.Th>
              <Table.Th style={{ width: '10%' }}></Table.Th>
              <Table.Th style={{ width: '25%', textAlign: 'right' }}>Total ml per Batch</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {container.elements.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={4} style={{ textAlign: 'center', padding: 24 }}>
                  <Text c="dimmed">No elements added yet. Use the dropdown below to add elements.</Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              container.elements.map((element, elIndex) => (
                <Table.Tr key={elIndex}>
                  <Table.Td>
                    <Badge variant="light" color={BADGE_COLORS[elIndex % BADGE_COLORS.length]}>
                      {element.element_name}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <NumberInput
                      value={element.ml_per_day}
                      onChange={(val) =>
                        onUpdateElement(index, elIndex, {
                          ml_per_day: Number(val) || 0,
                        })
                      }
                      min={0}
                      step={0.01}
                      decimalScale={2}
                      allowDecimal
                      allowNegative={false}
                      size="xs"
                      style={{ width: 70 }}
                      aria-label={`${element.element_name} ml per day`}
                    />
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={() => onRemoveElement(index, elIndex)}
                      aria-label={`Remove ${element.element_name}`}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                    {(element.ml_per_day * days).toFixed(1)} ml
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
          {container.elements.length > 0 && (
            <Table.Tfoot>
              <Table.Tr style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                <Table.Td colSpan={3}><Text fw={600} c="blue">RO/DI Water</Text></Table.Td>
                <Table.Td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                  <Text c={overAllocated ? 'red' : 'blue'}>
                    {rodiWater.toFixed(1)} ml
                    {overAllocated && ' (over-allocated!)'}
                  </Text>
                </Table.Td>
              </Table.Tr>
              <Table.Tr style={{ backgroundColor: 'var(--mantine-color-blue-1)' }}>
                <Table.Td colSpan={3}><Text fw={700}>Total Volume</Text></Table.Td>
                <Table.Td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                  <Text fw={700}>{containerTotal.toFixed(0)} ml</Text>
                </Table.Td>
              </Table.Tr>
            </Table.Tfoot>
          )}
        </Table>

        {/* Add Element */}
        <div style={{ marginTop: 16 }}>
          <ElementSelector
            onSelect={(name) => onAddElement(index, name)}
            existingElements={existingElements}
            placeholder="+ Add Element"
          />
        </div>
      </div>
    </Paper>
  );
}
